import type React from "react";
import { useState } from "react";
import { twJoin } from "tailwind-merge";

import { Send03Icon } from "@/components/Icons/Send03Icon";
import { IconButton } from "@/design-system/IconButton";
import { TextArea } from "@/design-system/TextArea";
import { useShouldSubmitOnEnter } from "@/hooks/useShouldSubmitOnEnter";
import { useTranslation } from "@/translations";

interface AskNobiChatInputProps {
  value: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  notice?: React.ReactNode;
  onChange: (value: string) => void;
  onSubmit: () => void;
  ref?: React.Ref<HTMLTextAreaElement>;
}

export function AskNobiChatInput({
  value,
  placeholder,
  disabled,
  error,
  notice,
  onChange,
  onSubmit,
  ref,
}: AskNobiChatInputProps): React.ReactNode {
  const { t } = useTranslation();
  const [emptySubmitError, setEmptySubmitError] = useState(false);
  const canSend = value.trim().length > 0 && !disabled;
  const hasValue = value.trim().length > 0;
  const shouldSubmitOnEnter = useShouldSubmitOnEnter();

  function handleSubmit() {
    if (canSend) {
      setEmptySubmitError(false);
      onSubmit();
    } else if (!disabled) {
      setEmptySubmitError(true);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey && shouldSubmitOnEnter) {
      event.preventDefault();
      handleSubmit();
    }
  }

  function handleChange(newValue: string) {
    if (emptySubmitError) {
      setEmptySubmitError(false);
    }

    onChange(newValue);
  }

  return (
    <section className="relative flex w-full flex-col gap-space-sm">
      {notice}
      <div
        className={twJoin(
          "flex w-full gap-card-compact rounded-xl border-hairline px-space-md py-card-compact caret-text-brand-tertiary shadow-message-field transition-colors",
          (error || emptySubmitError) &&
            !disabled &&
            "border-border-input-error bg-background-default text-text-primary",
          disabled && "border-border-input-disabled bg-background-input-disabled text-text-disabled",
          !error &&
            !emptySubmitError &&
            !disabled &&
            "bg-background-default text-text-primary focus-within:border-border-input-focus",
          !error &&
            !emptySubmitError &&
            !disabled &&
            (hasValue ? "border-border-input-filled" : "border-border-input-rest"),
        )}
      >
        <TextArea
          ref={ref}
          value={value}
          disabled={disabled}
          appearance="composer"
          height="composer"
          autoFocus
          enterKeyHint={shouldSubmitOnEnter ? "send" : "enter"}
          placeholder={placeholder}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <footer className="flex flex-col items-end justify-end">
          <IconButton
            onClick={handleSubmit}
            icon={<Send03Icon size={16} />}
            title={t("page.chat-detail.chat.input-tooltip")}
            isCircular
            variant="filled"
            size="sm"
            disabled={!canSend}
          />
        </footer>
      </div>
    </section>
  );
}
